<template>
<div class="feed-card feed__message-block">
  <div class="d-flex position-relative">
    <span class="v-line"></span>
    <span class="circle-step flex-shrink-0"></span>
    <div class="content">
      <p class="fw-semibold">{{ userActivity.beneficiary_geolocation_messages.title }}</p>

      <div class="mb-1">
        <div>
          <div>
            <template v-if="sharingStep === 'location'">
						  <SharePositionBtn v-if="!sharingPosition" @click="updatePosition" :errorCode="geolocationErrorCode" :sharePositionMessage="userActivity.beneficiary_geolocation_messages.share_position" />

              <template v-else>
                <span class="loading">
                  <span class="spinner-border spinner-border-sm"></span>
                  {{ $t('beneficiary_location.button_loading_content') }}
                </span>
                <p v-if="currentAccuracy !== null" class="text-muted small mt-2 mb-0">
                  {{ $t('beneficiary_location.accuracy_estimate', { accuracy: currentAccuracy }) }}
                </p>
              </template>
            </template>

            <template v-else-if="sharingStep === 'is_underground'">
              <p v-if="currentAccuracy !== null" class="text-muted small mb-2">
                {{ $t('beneficiary_location.accuracy_estimate', { accuracy: currentAccuracy }) }}
              </p>
              <p class="fw-semibold mb-3">{{ $t('beneficiary_location.ask.underground_question') }}</p>
              <div class="mb-2">
                <button @click="handleUndergroundChoice(true)" class="btn btn-light w-100 mb-2">
                  {{ $t('beneficiary_location.ask.underground_yes') }}
                </button>
              </div>
              <div class="mb-2">
                <button @click="handleUndergroundChoice(false)" class="btn btn-light w-100">
                  {{ $t('beneficiary_location.ask.underground_no') }}
                </button>
              </div>
            </template>

            <template v-else-if="sharingStep === 'manual'">
              <p class="fw-semibold mb-2">{{ $t('beneficiary_location.ask.manual_fallback_info') }}</p>
              <ManualGeolocation :userActivity="userActivity" :subscription="subscription" event="send_position" />
            </template>
          </div>
        </div>
      </div>

      <div v-if="userActivity.data.state !== 'rejection_confirmed' && sharingStep === 'location'" @click="declineGeolocation" class="mb-3">
        <button class="btn btn-light w-100">
          <XmarkSvg />
          <span> {{ userActivity.beneficiary_geolocation_messages.decline_geolocation }} </span>
        </button>
      </div>

      <TimeInfos :userActivity="userActivity" />
    </div>
  </div>
</div>
</template>

<script setup>
  import { onMounted, ref } from 'vue';
  import { loadGoogleMapFrontApi, setWithDefault, sendAcCommand } from '@lib/utilities';
  import XmarkSvg from '@images/XmarkSvg.vue';
  import TimeInfos from '@components/utils/TimeInfos.vue';
  import SharePositionBtn from './SharePositionBtn.vue'
  import ManualGeolocation from './ManualGeolocation.vue';

  const props = defineProps({
    userActivity: Object,
    subscription: Object,
  });

  // Target accuracy (in meters): stop refining as soon as it is reached.
  const ACCURACY_TARGET = 20;
  // Time budget for a single acquisition attempt (ms).
  const ACQUISITION_TIMEOUT = 15000;
  // Number of successive attempts before falling back to manual entry.
  const MAX_ATTEMPTS = 3;

  const isVisible = ref(false);
  const sharingPosition = ref(false)
  const geolocationErrorCode = ref(null)
  const sharingStep = ref('location')
  const currentPosition = ref(null)
  const currentAccuracy = ref(null)

  const handleUpdatePositionError = (error) => {
    geolocationErrorCode.value = error.code
	  sharingPosition.value = false
  };

  // Real-time acquisition: listen via watchPosition and keep the fix with the
  // best accuracy. Stop as soon as the target accuracy is reached, or when the
  // time budget elapses, returning the best fix obtained so far.
  const acquireBestPosition = () => new Promise((resolve, reject) => {
    let best = null;
    let settled = false;

    const finish = (error) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      clearTimeout(timer);
      best ? resolve(best) : reject(error || { code: 3 });
    };

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        if (!best || position.coords.accuracy < best.coords.accuracy) {
          best = position;
          currentAccuracy.value = Math.round(position.coords.accuracy);
        }
        if (best.coords.accuracy <= ACCURACY_TARGET) finish();
      },
      // Only fail if no fix has been obtained yet.
      (error) => { if (!best) finish(error); },
      { enableHighAccuracy: true, maximumAge: 0, timeout: ACQUISITION_TIMEOUT }
    );

    const timer = setTimeout(() => finish(), ACQUISITION_TIMEOUT);
  });

  const updatePosition = async () => {
    sharingPosition.value = true;
    geolocationErrorCode.value = null;
    currentAccuracy.value = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        currentPosition.value = await acquireBestPosition();
        sharingPosition.value = false;

        // The guided depollution parcours asks its own accessibility question
        // (with the truck photo) right after this widget, so skip the
        // redundant "hauteur limitée" sub-question here and send the position
        // directly.
        if (props.userActivity.data.guided) {
          sendGeolocation(currentPosition.value);
          sharingStep.value = null;
        } else {
          sharingStep.value = "is_underground";
        }
        return;
      } catch (error) {
        // Permission denied by the user: retrying is pointless.
        if (error.code === 1) {
          handleUpdatePositionError(error);
          return;
        }
      }
    }

    // Real-time geolocation unavailable after all attempts: manual entry.
    sharingPosition.value = false;
    sharingStep.value = "manual";
  };

  const sendGeolocation = async (position, isUnderground = false) => {
    geocodePosition(position).then((address) => {
      const addressParams = {is_underground: isUnderground,...address}
      const params = {
        event: 'send_position',
        id: props.userActivity.id,
        ...addressParams
      }

      sendAcCommand(props.subscription, 'update_beneficiary_geolocation', params)
    }).catch((err) => {
      console.error('Geocoding failed', err);
    });
  };

  const handleUndergroundChoice = (isUnderground) => {
    sendGeolocation(currentPosition.value, isUnderground);
    sharingStep.value = null;
  };

  const declineGeolocation = () => {
    let event;

    if (props.userActivity.data.state === 'initial') {
      event = 'reject'
    } else if(props.userActivity.data.state === 'rejected') {
      event = 'confirm_rejection'
    }

    sendAcCommand(props.subscription, 'update_beneficiary_geolocation', { id: props.userActivity.id, event: event })
  };

  const geocodePosition = async (position) => {
    const pos = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };

    const { Geocoder } = await google.maps.importLibrary('geocoding');
    const geocoder = new Geocoder();
    const response = await geocoder.geocode({ location: pos });
    const result = response.results[0];

    const address = result.address_components
      .filter(item =>
        item.types.some(type =>
          ['street_number', 'route', 'locality', 'country', 'postal_code'].includes(type)))
      .reduce((acc, item) => {
        acc[item.types[0]] = item.long_name;
        return acc;
      }, {});

    return {
      latitude: pos.lat,
      longitude: pos.lng,
      country: address.country,
      number: address.street_number,
      street_name: setWithDefault(address.route, address.street_address),
      city_name: address.locality,
      city_zip: address.postal_code
    };
  };

  onMounted(() => {
    if ('geolocation' in navigator) {
      isVisible.value = true;
      loadGoogleMapFrontApi();
    }
  });
</script>
